import { useState, useEffect, useCallback, useMemo } from 'react';
import DinamikFormConfigAPI from '../apis/OB/Get/DinamikFormConfigAPI';
import { IDinamikFormConfig, IFormAlanConfig, IAlanBagimlilik } from '../types/form-inputs/IDinamikFormConfig';
import { applyConfigToSchema } from '../helpers/DynamicValidationBuilder';
import * as yup from 'yup';

// Cache kaldirildi — her sayfa yuklenisinde API'den taze veri cekilir.
// Boylece admin panelden yapilan degisiklikler aninda yansir.

/**
 * Bagimlilik kosulunu degerlendirir.
 */
function evaluateCondition(bagimlilik: IAlanBagimlilik, formValues: any): boolean {
  const currentValue = formValues[bagimlilik.kosulAlanKodu];
  const kosulDegeri = bagimlilik.kosulDegeri;

  switch (bagimlilik.kosulOperatoru) {
    case 'EQ':
      return String(currentValue) === String(kosulDegeri);
    case 'NEQ':
      return String(currentValue) !== String(kosulDegeri);
    case 'GT':
      return Number(currentValue) > Number(kosulDegeri);
    case 'LT':
      return Number(currentValue) < Number(kosulDegeri);
    case 'IN': {
      if (!kosulDegeri) return false;
      const values = kosulDegeri.split(',').map(v => v.trim());
      return values.includes(String(currentValue));
    }
    case 'NOT_NULL':
      return currentValue != null && currentValue !== '';
    default:
      return false;
  }
}

export function useFormConfig(sayfaKodu: string) {
  const [config, setConfig] = useState<IDinamikFormConfig | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    DinamikFormConfigAPI.getFormConfig(sayfaKodu)
      .then((res: any) => {
        if (cancelled) return;
        const data = res?.data as IDinamikFormConfig;
        if (data && data.alanlar) {
          setConfig(data);
        } else {
          setConfig(null);
        }
      })
      .catch(() => {
        if (!cancelled) setConfig(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [sayfaKodu]);

  /**
   * Alanin gorunur olup olmadigini doner.
   * Config yoksa her zaman true (fallback).
   */
  const isFieldVisible = useCallback((fieldName: string, formValues: any): boolean => {
    if (!config) return true;

    const alan = config.alanlar.find(a => a.alanKodu === fieldName);
    if (!alan) return true;

    // Temel gorunurluk
    if (alan.gorunurMu === 'H') return false;

    // Bagimlilik kurallari
    if (alan.bagimliliklar && alan.bagimliliklar.length > 0) {
      for (const bag of alan.bagimliliklar) {
        const conditionMet = evaluateCondition(bag, formValues);
        if (conditionMet) {
          if (bag.eylem === 'GIZLE') return false;
        } else {
          if (bag.eylem === 'GOSTER') return false;
        }
      }
    }

    return true;
  }, [config]);

  /**
   * Alanin zorunlu olup olmadigini doner.
   * Config yoksa null (mevcut schema'ya dokunma).
   */
  const isFieldRequired = useCallback((fieldName: string, formValues: any): boolean | null => {
    if (!config) return null;

    const alan = config.alanlar.find(a => a.alanKodu === fieldName);
    if (!alan) return null;

    let required = alan.zorunluMu === 'E';

    // Bagimlilik kurallari
    if (alan.bagimliliklar && alan.bagimliliklar.length > 0) {
      for (const bag of alan.bagimliliklar) {
        const conditionMet = evaluateCondition(bag, formValues);
        if (conditionMet) {
          if (bag.eylem === 'ZORUNLU_YAP') required = true;
          if (bag.eylem === 'OPSIYONEL_YAP') required = false;
        }
      }
    }

    return required;
  }, [config]);

  /**
   * Alan etiketini doner. Config yoksa fallback kullanilir.
   * Zorunlu alanlarin labelinin sonuna " *" eklenmez — bu CustomTextField tarafindan halledilir.
   */
  const getFieldLabel = useCallback((fieldName: string, fallback: string): string => {
    if (!config) return fallback;

    const alan = config.alanlar.find(a => a.alanKodu === fieldName);
    if (!alan) return fallback;

    // Config'den gelen etikete zorunluluk isaretini ekle
    const label = alan.alanEtiketi || fallback;
    if (alan.zorunluMu === 'E') {
      return label.endsWith('*') ? label : label + ' *';
    }
    return label;
  }, [config]);

  /**
   * Mevcut Yup schema'yi config'e gore modifiye eder.
   * Config yoksa orijinal schema'yi doner.
   */
  const buildValidationSchema = useCallback((baseSchema: yup.ObjectSchema<any>): yup.ObjectSchema<any> => {
    return applyConfigToSchema(baseSchema, config);
  }, [config]);

  /**
   * Alan config bilgilerini doner.
   */
  const getFieldProps = useCallback((fieldName: string): Partial<IFormAlanConfig> | null => {
    if (!config) return null;
    return config.alanlar.find(a => a.alanKodu === fieldName) || null;
  }, [config]);

  return {
    config,
    isLoading,
    isFieldVisible,
    isFieldRequired,
    getFieldLabel,
    buildValidationSchema,
    getFieldProps,
  };
}
